/**
 * Authentication Context
 * Manages authentication state and provides auth methods
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { showSuccess, showError, showInfo } from '../utils/toastConfig';
import { msalInstance, msalReady, loginRequest } from '../services/msalConfig';
import {
  ACTIVATION_ROLES,
  ACTIVATION_ONLY_ROLES,
  ACTIVATION_TEAM_ROLES,
} from '../constants/activationRoles';
import { clearProjectionSessionCaches } from '../pages/budgetProjections/projectionSessionCache';

const AuthContext = createContext({});

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '/api';

/**
 * Set auth token in axios headers
 * @param {string} token - JWT token
 */
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
    // Signing out (logout, or a stored token the API rejects): the Projections V2
    // sessionStorage caches are keyed by projection, not by user.
    clearProjectionSessionCaches();
  }
};

/**
 * Authentication Provider Component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Load user from token on mount
   */
  useEffect(() => {
    loadUser();
  }, []);

  /**
   * Load user from stored token
   */
  const loadUser = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      setAuthToken(token);
      
      try {
        const response = await axios.get('/auth/me');
        if (response.data.success) {
          setUser(response.data.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error loading user:', error);
        // Token might be expired or invalid
        setAuthToken(null);
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  };

  /**
   * User login
   * @param {string} username - Username or email
   * @param {string} password - Password
   * @returns {Promise<boolean>} - Success status
   */
  const login = async (username, password) => {
    try {
      const response = await axios.post('/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;

        // A new sign-in starts without the previous account's cached figures: the 401
        // handler in services/apiClient.js signs out without calling setAuthToken(null).
        clearProjectionSessionCaches();
        setAuthToken(token);
        
        // Store user data
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        
        showSuccess('Login successful!');
        return true;
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      showError(message);
      return false;
    }
  };

  /**
   * Microsoft SSO login
   * Opens Microsoft popup and sends ID token to backend
   * @returns {Promise<boolean>} - Success status
   */
  const microsoftLogin = async () => {
    try {
      await msalReady;
      const response = await msalInstance.loginPopup(loginRequest);

      const { data } = await axios.post('/auth/microsoft-login', {
        idToken: response.idToken
      });

      if (data.success) {
        const { token, user } = data.data;
        // A new sign-in, as in login().
        clearProjectionSessionCaches();
        setAuthToken(token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        showSuccess('Login successful!');
        return true;
      }
    } catch (error) {
      // User cancelled the popup
      if (error.errorCode === 'user_cancelled') {
        return false;
      }
      const message = error.response?.data?.message || error.message || 'Microsoft login failed';
      showError(message);
      return false;
    }
  };

  /**
   * User logout
   */
  const logout = () => {
    // Clear auth data
    setAuthToken(null);
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    
    showInfo('Logged out successfully');
  };

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<boolean>} - Success status
   */
  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/auth/profile', profileData);
      
      if (response.data.success) {
        const updatedUser = response.data.data;
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        showSuccess('Profile updated successfully');
        return true;
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile';
      showError(message);
      return false;
    }
  };

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<boolean>} - Success status
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      if (response.data.success) {
        showSuccess('Password changed successfully');
        return true;
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password';
      showError(message);
      return false;
    }
  };

  /**
   * Check if user has required role
   * @param {...string} roles - Required roles
   * @returns {boolean} - Has permission
   */
  const hasRole = (...roles) => {
    if (!user) return false;
    if (roles.includes(user.role)) return true;
    // `reporting_manager` is a specialised role created for the Sales Reporting
    // / Reporting Groups feature. It implies at least standard 'user'-level
    // read access across all modules (Master Data, dashboards, etc.) so the
    // user can navigate the app and look up the data their reports cover. The
    // role's extra WRITE privileges (Sales Reporting runs, Reporting Group
    // CRUD, Brand emailTo/Cc edits) are enforced on the backend by the
    // dedicated `authorize('reporting_manager', ...)` middleware. Without this
    // alias the role would be rejected at every standard route that lists
    // 'user' in requiredRoles — including the very pages it was created for.
    if (user.role === 'reporting_manager' && roles.includes('user')) return true;
    return false;
  };

  /**
   * Check if user is admin
   * @returns {boolean} - Is admin
   */
  const isAdmin = () => {
    return hasRole('admin');
  };

  /**
   * Check if user is manager or above
   * @returns {boolean} - Is manager or admin
   */
  const isManager = () => {
    return hasRole('admin', 'manager');
  };

  /**
   * Check if user is brand manager
   * @returns {boolean} - Is brand manager
   */
  const isBrandManager = () => {
    return hasRole('brand_manager');
  };

  /**
   * Check if user has marketing access
   *
   * Includes the activation team roles, because this predicate gates the whole
   * Marketing nav group — without them the team roles would have no route into
   * Marketing > Activation at all. The group's other entries carry their own
   * `show: !activationOnly` predicate, so a VM user sees only Activation.
   * @returns {boolean} - Has marketing access
   */
  const hasMarketingAccess = () => {
    return hasRole('admin', 'manager', 'brand_manager', ...ACTIVATION_TEAM_ROLES);
  };

  /**
   * Check if user may open the Activations module
   * @returns {boolean} - Has activation access
   */
  const hasActivationAccess = () => {
    return hasRole(...ACTIVATION_ROLES);
  };

  /**
   * Check if Marketing > Activation is the user's ONLY screen. Collapses the
   * sidebar the same way isBrandManager() does.
   * @returns {boolean} - Is limited to the Activations module
   */
  const isActivationOnly = () => {
    return hasRole(...ACTIVATION_ONLY_ROLES);
  };

  /**
   * Check if user has full supplier access (admin or manager)
   * @returns {boolean} - Has full supplier access
   */
  const hasFullSupplierAccess = () => {
    return hasRole('admin', 'manager');
  };

  /**
   * Get assigned suppliers for brand manager
   * @returns {Array} - List of assigned supplier IDs
   */
  const getAssignedSuppliers = () => {
    if (!user) return [];
    return user.suppliers || [];
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    microsoftLogin,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    isAdmin,
    isManager,
    isBrandManager,
    hasMarketingAccess,
    hasActivationAccess,
    isActivationOnly,
    hasFullSupplierAccess,
    getAssignedSuppliers,
    loadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 * @returns {Object} - Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;

